import React, { FC } from 'react';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';
import Edit from '@mui/icons-material/Edit';

import {
  ColumnProps,
  DropdownMenuItem,
  Page,
  Table,
  useDialogState,
  useFilterConfig,
  usePanelState,
  useTableQueryParams,
} from 'components';
import { FilterOptionModel, PermissionGroupModel, QueryParamModel } from 'models';
import { usePermissionGroupList } from 'features/permissionGroups';
import { hasPermission } from 'utils/auth';
import { selectUserPermissions } from 'store/profile';
import { getPageName } from 'utils/format';
import { formatDate } from 'utils/dates';
import { parse, stringify } from 'utils/base64';
import { RouteNames } from 'config/routeNames';
import LeoCheckBox from 'components/input-fields/leoCheckBox/LeoCheckBox';

import PermissionGroupCreateDialog from './PermissionGroupCreateDialog';

const PermissionGroupsPage: FC = () => {
  const navigate = useNavigate();
  const permissions = useSelector(selectUserPermissions);
  const canEdit = hasPermission(permissions, { scope: 'PERMISSION_GROUPS', mode: 'edit' });

  const {
    onPageChange,
    onRowsPerPageChange,
    onSort,
    onFilter,
    onLoadQuery,
    params,
    apiParams,
    onTableColumnVisibilityChange,
  } = useTableQueryParams({
    order: undefined,
  });

  // The checkbox filter serialises to `['on']`; the API expects a boolean.
  const apiParamsWithInactive = (paramsToConvert: Partial<QueryParamModel>) => {
    const parsedFilter = parse(paramsToConvert?.filter);
    if (parsedFilter?.showInactive && parsedFilter.showInactive[0] === 'on') {
      return { ...paramsToConvert, filter: stringify({ ...parsedFilter, showInactive: true }) };
    }
    return paramsToConvert;
  };

  const {
    data: permissionGroupsData,
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = usePermissionGroupList(apiParamsWithInactive(apiParams));

  const { activePanel, closePanel, setPanel } = usePanelState();

  const {
    isOpen: isCreateOpen,
    openEmptyDialog: openCreate,
    closeDialog: closeCreate,
  } = useDialogState();

  const filters: FilterOptionModel[] = [
    {
      headerTitle: 'KOZOS_JOGOSULTSAGI_CSOPORTOK.SHOW_INACTIVE',
      component: (
        <LeoCheckBox
          name="showInactive"
          label={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.SHOW_INACTIVE" />}
        />
      ),
      name: 'showInactive',
      abbrName: t('FILTERABBR.INACTIVES'),
      panelFieldWidth: 12,
    },
  ];

  const filterConfig = useFilterConfig({
    params,
    onFilter,
    filters,
    onLoad: onLoadQuery,
    isButton: true,
    pageName: getPageName(RouteNames.KOZOS_JOGOSULTSAGI_CSOPORTOK),
    isFetching,
  });

  const panels = [filterConfig];

  const getDetailsPath = (item: PermissionGroupModel) =>
    `/${generatePath(RouteNames.KOZOS_JOGOSULTSAGI_CSOPORTOK_RESZLETEK, {
      id: item.id.toString(),
    })}`;

  const navigateToDetails = (item: PermissionGroupModel) => navigate(getDetailsPath(item));

  const columns: ColumnProps<PermissionGroupModel>[] = [
    {
      key: 'name',
      header: <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.NAME" />,
      accessor: 'name',
      field: 'name',
      sortable: true,
    },
    {
      key: 'memberCount',
      header: <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.MEMBER_COUNT" />,
      accessor: 'memberCount',
      field: 'memberCount',
    },
    {
      key: 'isActive',
      header: <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.STATE" />,
      accessor: (item) => (
        <Trans
          i18nKey={
            item.isActive
              ? 'KOZOS_JOGOSULTSAGI_CSOPORTOK.ACTIVE'
              : 'KOZOS_JOGOSULTSAGI_CSOPORTOK.INACTIVE'
          }
        />
      ),
      field: 'isActive',
      sortable: true,
    },
    {
      key: 'updatedAt',
      header: <Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.LAST_MODIFICATION" />,
      accessor: (item) => formatDate(item.updatedAt),
      field: 'updatedAt',
      sortable: true,
    },
  ];

  const actions: DropdownMenuItem[] = [
    {
      name: 'edit',
      label: 'COMMON.EDIT',
      icon: <Edit fontSize="small" />,
      onClick: (item: PermissionGroupModel) => navigateToDetails(item),
    },
  ];

  return (
    <Page
      title={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.TITLE" />}
      inScroll
      activePanel={activePanel}
      closePanel={closePanel}
      setPanel={setPanel}
      panels={panels}
    >
      <Box>
        <Table
          panels={panels}
          setPanel={setPanel}
          activePanel={activePanel}
          timestamp={dataUpdatedAt}
          list={permissionGroupsData?.data ?? []}
          total={permissionGroupsData?.meta.total ?? 0}
          loading={isLoading}
          params={params}
          onSort={onSort}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          columns={columns}
          actions={actions}
          mapActions
          enableCheckbox={false}
          onAdd={canEdit ? () => openCreate() : undefined}
          addLabel={<Trans i18nKey="KOZOS_JOGOSULTSAGI_CSOPORTOK.NEW" />}
          onRowDoubleClick={navigateToDetails}
          onTableColumnVisibilityChange={onTableColumnVisibilityChange}
          onLoadQuery={onLoadQuery}
        />
      </Box>
      <PermissionGroupCreateDialog open={isCreateOpen} onClose={closeCreate} />
    </Page>
  );
};

export default PermissionGroupsPage;
